import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useDataProvider, useNotify } from 'react-admin';
import {
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import PersonAddOutlinedIcon from '@material-ui/icons/PersonAddOutlined';
import CloseIcon from '@material-ui/icons/Close';
import moment from 'moment';

import { computeNextSilaeId } from '../../../helpers';

const useStyles = makeStyles((theme) => {
  return {
    title: {
      paddingBottom: theme.spacing(1),
    },
    titleRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    closeBtn: {
      marginRight: -theme.spacing(1),
    },
    subtitle: {
      color: theme.palette.text.secondary,
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
      paddingBottom: theme.spacing(2),
    },
    card: {
      padding: theme.spacing(2.5),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 12,
      transition: 'border-color 150ms ease, box-shadow 150ms ease',
      '&:hover': {
        borderColor: theme.palette.primary.main,
        boxShadow: '0 4px 16px rgba(50, 108, 180, 0.08)',
      },
    },
    cardClickable: {
      width: '100%',
      display: 'block',
      textAlign: 'inherit',
      cursor: 'pointer',
      '&:disabled': {
        cursor: 'default',
        opacity: 0.6,
      },
    },
    cardHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5),
      marginBottom: theme.spacing(0.5),
    },
    iconBadge: {
      width: 40,
      height: 40,
      borderRadius: 10,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(50, 108, 180, 0.1)',
      color: theme.palette.primary.main,
      flexShrink: 0,
    },
    cardTitle: {
      fontWeight: 600,
      lineHeight: 1.3,
    },
    cardHint: {
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(2),
    },
    spacer: {
      height: theme.spacing(2),
    },
  };
});

const CreateCleanerDialog = ({ open, onClose }) => {
  const classes = useStyles();
  const history = useHistory();
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const [search, setSearch] = useState('');
  const [options, setOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [creatingNew, setCreatingNew] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setOptionsLoading(true);
    dataProvider
      .getList('cleaner', {
        sort: { field: 'contract_end_date', order: 'DESC' },
        pagination: { page: 1, perPage: 25 },
        filter: search ? { fullname: search } : {},
      })
      .then(({ data }) => {
        if (!cancelled) setOptions(data || []);
      })
      .catch(() => {
        if (!cancelled) setOptions([]);
      })
      .finally(() => {
        if (!cancelled) setOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [search, dataProvider, open]);

  const handleDuplicate = (cleaner) => {
    if (!cleaner) return;
    onClose();
    history.push(
      `/cleaner/create?duplicate_from=${encodeURIComponent(cleaner.id)}`,
    );
  };

  const handleCreateNew = async () => {
    setCreatingNew(true);
    try {
      const silaeId = await computeNextSilaeId(dataProvider);
      onClose();
      history.push(`/cleaner/create?new_silae_id=${silaeId}`);
    } catch (err) {
      notify(
        err.message || 'Erreur lors du calcul du silae_id',
        'warning',
        null,
        false,
        10000,
      );
    } finally {
      setCreatingNew(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle disableTypography className={classes.title}>
        <div className={classes.titleRow}>
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            Nouvelle fiche cleaner
          </Typography>
          <IconButton
            size="small"
            className={classes.closeBtn}
            onClick={onClose}
            disabled={creatingNew}
            aria-label="Fermer"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </DialogTitle>
      <Typography variant="body2" className={classes.subtitle}>
        Choisis comment initialiser la fiche.
      </Typography>
      <DialogContent>
        <Paper variant="outlined" className={classes.card} elevation={0}>
          <div className={classes.cardHeader}>
            <div className={classes.iconBadge}>
              <FileCopyOutlinedIcon />
            </div>
            <Typography variant="subtitle1" className={classes.cardTitle}>
              Dupliquer un cleaner existant
            </Typography>
          </div>
          <Typography variant="body2" className={classes.cardHint}>
            Recopie l&apos;intégralité des champs depuis une fiche source.
          </Typography>
          <Autocomplete
            options={options}
            getOptionLabel={(option) => {
              if (!option) return '';
              if (option.contract_end_date) {
                return `${option.fullname} — ${moment(
                  option.contract_end_date,
                ).format('DD/MM/YY')}`;
              }
              return option.fullname || '';
            }}
            getOptionSelected={(option, value) => {
              return option && value && option.id === value.id;
            }}
            loading={optionsLoading}
            onInputChange={(_, value) => {
              return setSearch(value);
            }}
            onChange={(_, value) => {
              return handleDuplicate(value);
            }}
            renderInput={(params) => {
              return (
                <TextField
                  {...params}
                  placeholder="Rechercher un cleaner…"
                  variant="outlined"
                  size="small"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {optionsLoading ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              );
            }}
          />
        </Paper>

        <div className={classes.spacer} />

        <ButtonBase
          component="div"
          focusRipple
          disabled={creatingNew}
          onClick={handleCreateNew}
          className={classes.cardClickable}
        >
          <Paper variant="outlined" className={classes.card} elevation={0}>
            <div className={classes.cardHeader}>
              <div className={classes.iconBadge}>
                {creatingNew ? (
                  <CircularProgress size={20} thickness={2} color="inherit" />
                ) : (
                  <PersonAddOutlinedIcon />
                )}
              </div>
              <Typography variant="subtitle1" className={classes.cardTitle}>
                {creatingNew ? 'Initialisation…' : 'Démarrer une fiche vierge'}
              </Typography>
            </div>
            <Typography
              variant="body2"
              className={classes.cardHint}
              style={{ marginBottom: 0 }}
            >
              Pré-remplit le silae Id.
            </Typography>
          </Paper>
        </ButtonBase>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={creatingNew} color="default">
          Annuler
        </Button>
      </DialogActions>
    </Dialog>
  );
};

CreateCleanerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default CreateCleanerDialog;
